import { Close } from "@mui/icons-material"
import { Badge, Button, CircularProgress, Divider, Grid2, IconButton, Skeleton, TextField, Typography, useTheme } from "@mui/material"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import OrderStepper from "../../../components/OrderStepper"
import { IOrder, IOrderSocketResponse } from "sierra"
import api from "../../../utils/axios"
import socket from "../../../utils/socket"
import OrderManageSkeleton from "../../../components/Skeletons/OrderManageSkeleton"
import { useApp } from "../../../contexts/AppContext"

interface OutletContextType {
    setOpenOrderChat: React.Dispatch<React.SetStateAction<boolean>>;
    openOrderChat: boolean;
}


const DashboardOrderManage = memo(() => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { setOpenOrderChat } = useOutletContext<OutletContextType>()
    const [searchParams] = useSearchParams()
    const {translate, lang} = useApp()

    const order_id = searchParams.get('order_id');

    const [order, setOrder] = useState<IOrder>()
    const [apiCall, setApiCall] = useState<boolean>(false)
    const [stepIndex, setStepIndex] = useState<number>(0)
    const [unreadCount, setUnreadCount] = useState(0)

    const completedSteps = useMemo(() => {
        if (!order || !order.order_details) return
        return order.order_details.map(s => (
            s.fields
                && s.fields[0]
                && s.fields.every(f =>
                    f.field_required === false ? true :
                        !f.field_value ? false :
                            f.field_type === 'multiselect' && Array.isArray(f.field_value) && f.field_value.length === 0 ? false : true
                )
                ? true : false
        ))
    }
        , [order])

    const fetchOrder = () => {
        api.get(`/orders`, { params: { order_id, exclude_fields: 'service_form' } }).then(({ data }) => setOrder(data[0])).catch(console.error)
    }

    const handleUpdateOrder = useCallback(() => {
        if (!order || !order.order_details) return
        setApiCall(true)
        api.patch(`/orders/${order_id}/details/${order.order_details[stepIndex].step_id}`, { fields: order.order_details[stepIndex].fields.map(f => ({ field_id: f.field_id, field_value: f.field_value || null })) }, { meta: { showSnackbar: true } }).then(() => {
            setApiCall(false)
            fetchOrder()
            // clearForm()
        }).catch((err) => {
            console.log(err);
            setApiCall(false)
            // clearForm()
        })
    }, [order])

    const handleOrderListener = useCallback((data: IOrderSocketResponse[]) => {
        if (!order_id) return
        const changedOrderIds = data.map(order => order.order_id);
        const shouldFetch = changedOrderIds.includes(parseInt(order_id))
        if (shouldFetch) {
            fetchOrder();
        }
    }, [])

    const fetchUnreadCount = (data?: Array<{ message_id: number, order_id: number }>) => {
        console.log('fetchUnreadCount', data)
        if (data && !data.find(d => d.order_id === Number(order_id))) return;
        api.get(`/ordersChat/${order_id}/unread`).then(({ data }) => setUnreadCount(data.count)).catch(console.error)
    }


    useEffect(() => {
        fetchOrder()
        fetchUnreadCount()
        socket.on("orders_changed", handleOrderListener);
        socket.on("orders_messages_changed", fetchUnreadCount);
        return () => {
            socket.off("orders_changed", handleOrderListener);
            socket.off("orders_messages_changed", fetchUnreadCount);
        };
    }, [order_id])


    return (
        <Grid2 container size={'grow'} height={'100%'} padding={2} flexDirection={'column'} spacing={0}>


            {!order || !order.order_details || !completedSteps ? <OrderManageSkeleton /> :

                <>
                    <Grid2 container size={'auto'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>

                        <Grid2>
                            <Typography variant='h5'>
                                {order.is_cancelled || order.order_status === 'completed' ? translate('View Order') : translate('Manage Order')} # {order.order_id}
                            </Typography>
                        </Grid2>

                        <Grid2>
                            <IconButton onClick={() => {
                                navigate('/dashboard/orders')
                                setOpenOrderChat(false)
                            }}>
                                <Close />
                            </IconButton>
                        </Grid2>

                    </Grid2>

                    <Grid2 display={'flex'} justifyContent={'center'}>
                        <Typography variant="body2" color={order.order_status === 'validating' ? "info" : order.order_status === 'pending' ? 'error' : order.order_status === 'completed' ? 'success' : ''}>{
                            order.is_cancelled ? translate('Your order has been cancelled') :
                                order.order_status === 'validating' ? `${translate("Your order has been created on")} ${new Date(order.creation_timestamp).toLocaleString(lang === 'ar' ? 'ar-SD' : undefined)} ${translate("and is being validated")}`
                                    : order.order_status === 'pending' ? translate(`Your order requires an update`)
                                        : order.order_status === 'completed' ? translate(`Your order has been completed`) : ``
                        }</Typography>
                    </Grid2>

                    <Grid2 size={'grow'}>
                        <OrderStepper form={order.order_details} setForm={(updatedForm) =>
                            setOrder((prev) =>
                                prev
                                    ? { ...prev, order_details: typeof updatedForm === 'function' ? updatedForm(prev.order_details) : updatedForm }
                                    : prev
                            )} setStepIndex={setStepIndex} />
                    </Grid2>

                    <Grid2 container display={'fle'} alignItems={'center'} justifyContent={'space-between'} paddingTop={2}>
                        <Grid2 onClick={() => setOpenOrderChat(true)}>
                            <Badge badgeContent={unreadCount} color="error">
                                <Button variant="contained">{translate("Customer Support")}</Button>
                            </Badge>
                        </Grid2>

                        <Grid2 container display={'flex'} alignItems={'center'} spacing={2} justifyContent={'end'}>
                            <Grid2>
                                <Typography sx={{ paddingTop: '2px' }} fontWeight={'bold'}><span style={{ color: theme.palette.secondary.dark }}>{translate("Total Price")}: </span>{(order.order_amount_total / 100).toFixed(2)} {translate("SAR")}</Typography>
                            </Grid2>
                            <Grid2 display={order.is_cancelled ? 'none' : order.order_details[stepIndex].step_status === 'pending' ? 'flex' : 'none'}>
                                <Button variant='contained' onClick={handleUpdateOrder} disabled={completedSteps.some(step => !step) || apiCall} endIcon={apiCall ? <CircularProgress /> : null} >
                                    {translate("Update Step Data")}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </>}


        </Grid2>
    )
})

export default DashboardOrderManage